import { DashboardLayout } from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ChevronLeft, ChevronRight, Clock, Trash2, Eye, ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedback } from "@/hooks/useFeedback";
import {
  auditActorDisplay, useAuditLog, useAuditLogTables, usePlatformUpdates,
  type AuditActorType, type AuditLogFilters,
} from "@/hooks/useChangelog";
import { supabase } from "@/integrations/supabase/client";
import { deriveLoadState } from "@/lib/queryLoadState";
import { QueryLoadError } from "@/components/QueryLoadError";
import type { Tables } from "@/integrations/supabase/types";
import { format } from "date-fns";
import { useState } from "react";

type FeedbackRow = Tables<"user_feedback">;

/* ── Status helpers ── */
const statusColor: Record<string, string> = {
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  in_progress: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  done: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
};

const priorityColor: Record<string, string> = {
  low: "border-muted-foreground/30",
  medium: "border-yellow-500",
  high: "border-destructive",
};

const categoryLabel: Record<string, string> = {
  bug: "Bug",
  feature: "Feature",
  content_change: "Photo / content change",
  ui_ux: "UI / UX",
  other: "Other",
};

/* ── Audit Log Tab ── */
const ALL = "all";

const actorTypeOptions: { value: AuditActorType; label: string }[] = [
  { value: "user", label: "Staff users" },
  { value: "system", label: "System" },
  { value: "customer", label: "Customers" },
];

export function AuditLogTab() {
  const [filters, setFilters] = useState<AuditLogFilters>({});
  // The page belongs to the filters it was picked under, so a filter change
  // starts back on page 1 without fetching the old offset (#1192, as #1165).
  const filtersKey = JSON.stringify(filters);
  const [pageState, setPageState] = useState({ key: filtersKey, page: 0 });
  const page = pageState.key === filtersKey ? pageState.page : 0;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const { entriesQuery } = useAuditLog(filters, page);
  const tablesQuery = useAuditLogTables();
  const { data, isPending, isError, isPaused, isFetching, isPlaceholderData, refetch } = entriesQuery;
  const entries = data?.entries ?? [];
  const hasMore = data?.hasMore ?? false;
  // A failed table list leaves only "All tables"; the log itself still loads.
  const tables = tablesQuery.data ?? [];
  const filtered = Boolean(filters.entityTable || filters.actorType) || page > 0;

  // `isLoading` is NOT the right predicate here. React Query derives it as
  // `isPending && isFetching` (query-core 5.102.8, queryObserver.js:240), so a query
  // that cannot reach the network — networkMode "online", fetchStatus "paused", which
  // is what wifi off / airplane mode / DevTools Offline produces — reports isLoading
  // false, isError false and undefined data. Gating on isLoading would let that case
  // fall through to the empty row, i.e. #674 all over again. Routing it to the spinner
  // instead would show a skeleton until the connection came back with nothing on screen
  // explaining why. It gets the failed row, with copy of its own.
  // `isPending && isPaused`, not bare `isPaused`: a paused *refetch* that already has
  // data is status "success" + paused, and would otherwise stamp an error under a table
  // full of good rows.
  const loadState = deriveLoadState({ isPending, isError, isPaused });
  const { loading, failed } = loadState;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select
          value={filters.entityTable ?? ALL}
          onValueChange={(v) => setFilters((f) => ({ ...f, entityTable: v === ALL ? undefined : v }))}
        >
          <SelectTrigger className="w-[200px]" aria-label="Filter by table">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All tables</SelectItem>
            {tables.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.actorType ?? ALL}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, actorType: v === ALL ? undefined : (v as AuditActorType) }))
          }
        >
          <SelectTrigger className="w-[180px]" aria-label="Filter by actor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All actors</SelectItem>
            {actorTypeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/*
                Three outcomes, three rows. Collapsing them (#674) meant a failed
                changelog_entries read — a revoked grant, an RLS regression, a dead
                network — rendered as "No activity logged yet.", i.e. a broken audit log
                that looks like a quiet one. The retry matters because
                refetchOnWindowFocus is off (App.tsx), so a failure persists for as long
                as the user stays on the tab.

                These rows sit ABOVE entries.map on purpose. React Query keeps the last
                good data when a refetch fails, and a page holds up to 100 rows — a
                notice rendered underneath them would be below the fold, which is the same
                "looks fine, actually broken" failure #674 is about.
              */}
              {loading && (
                <TableRow data-testid="audit-log-loading">
                  <TableCell colSpan={4} className="py-12">
                    <Skeleton className="h-4 w-full" aria-hidden="true" />
                  </TableCell>
                </TableRow>
              )}
              {failed && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <QueryLoadError
                      failedText="Couldn't load the audit log."
                      offlineText="You're offline. The audit log will load when your connection is back."
                      state={loadState}
                      query={{ isFetching, refetch }}
                    />
                  </TableCell>
                </TableRow>
              )}
              {entries.length === 0 && !loading && !failed && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                    {filtered ? "No activity matches these filters." : "No activity logged yet."}
                  </TableCell>
                </TableRow>
              )}
              {entries.map((entry) => {
                const actor = auditActorDisplay(entry);
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                      {format(new Date(entry.created_at), "MMM d, h:mm a")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {actor.kind === "user" ? (
                        <span>{actor.label}</span>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">{actor.label}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {entry.title}
                      {entry.entity_table && (
                        <span className="block text-[10px] font-normal text-muted-foreground">
                          {entry.entity_table}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px]">
                      <span className="block truncate">{entry.description || "—"}</span>
                      {entry.changed_columns && entry.changed_columns.length > 0 && (
                        <span className="block break-words">
                          Changed: {entry.changed_columns.join(", ")}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Page {page + 1}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0 || isPlaceholderData}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Newer
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={!hasMore || isPlaceholderData}
            >
              Older
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── What's New Tab (everyone) ── */
export function WhatsNewTab() {
  const { updatesQuery } = usePlatformUpdates();
  const { data, isPending, isError, isPaused, isFetching, refetch } = updatesQuery;
  const updates = data ?? [];
  const { loading, failed, offline, canRetry } = deriveLoadState({ isPending, isError, isPaused });

  return (
    <Card>
      <CardContent className="p-6">
        {/*
          Three outcomes, three branches. Reading only `data ?? []` (#755) meant a failed
          platform_updates read rendered as "No updates published yet." — a broken feed that
          looks like a quiet one.

          The notice sits ABOVE the timeline on purpose. React Query keeps the last good data
          when a refetch fails and this query pulls up to 200 rows, so a notice underneath them
          would land below the fold — the same "looks fine, actually broken" shape #674/#755
          exist to remove.
        */}
        {loading && (
          <div data-testid="whats-new-loading" className="space-y-4 py-2">
            <Skeleton className="h-4 w-2/3" aria-hidden="true" />
            <Skeleton className="h-4 w-1/2" aria-hidden="true" />
          </div>
        )}
        {failed && (
          <QueryLoadError
            className="text-center py-12"
            failedText="Couldn't load What's New."
            offlineText="You're offline. What's New will load when your connection is back."
            state={{ offline, canRetry }}
            query={{ isFetching, refetch }}
          />
        )}
        {!loading && !failed && updates.length === 0 && (
          <p className="text-center text-muted-foreground py-12 text-sm">
            No updates published yet — improvements ship regularly, so check back soon.
          </p>
        )}
        {updates.length > 0 && (
          <ol className="relative ml-3 space-y-6 border-l border-border">
            {updates.map((u) => (
              <li key={u.id} className="ml-6">
                <span
                  className={`absolute -left-[7px] mt-1 h-3.5 w-3.5 rounded-full ring-4 ring-background ${
                    u.category === "feature" ? "bg-primary" : "bg-amber-500"
                  }`}
                  aria-hidden
                />
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {u.category === "feature" ? "New" : "Fix"}
                  </Badge>
                  <time className="text-xs text-muted-foreground">
                    {format(new Date(u.committed_at), "MMM d, yyyy · h:mm a")}
                  </time>
                </div>
                <p className="text-sm text-foreground mt-1">{u.title}</p>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Feedback Tab (admin only) ── */
export function FeedbackTab() {
  const { feedbackQuery, updateStatus, deleteFeedback } = useFeedback();
  const [detailItem, setDetailItem] = useState<FeedbackRow | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<{ screenshot?: string; extra?: string }>({});
  const [showDone, setShowDone] = useState(false);

  // Aliased: openDetail below already binds `data` twice from createSignedUrl.
  const { data: feedbackData, isPending, isError, isPaused, isFetching, refetch } = feedbackQuery;
  const { loading, failed, offline, canRetry } = deriveLoadState({ isPending, isError, isPaused });
  const allFeedback = feedbackData ?? [];
  const feedback = showDone ? allFeedback : allFeedback.filter((fb) => fb.status !== "done");
  // The count must gate on the SAME list it prints — `feedback`, the filtered view — not
  // `allFeedback`. `loading` alone is not enough: on a FAILED load `loading` is false and the
  // displayed list is empty, which would render "0 items" directly above "Couldn't load
  // feedback." That includes a failed refetch retaining only `done` rows under the Open filter,
  // where `allFeedback` is non-empty but `feedback` is not (#791). A failed refetch that still
  // shows rows under the current filter keeps its real count, which is why this is not simply
  // `failed`.
  const showCount = !loading && !(failed && feedback.length === 0);

  async function openDetail(fb: FeedbackRow) {
    setDetailItem(fb);
    const urls: { screenshot?: string; extra?: string } = {};
    if (fb.screenshot_path) {
      const { data } = await supabase.storage.from("feedback-screenshots").createSignedUrl(fb.screenshot_path, 3600);
      if (data?.signedUrl) urls.screenshot = data.signedUrl;
    }
    if (fb.extra_screenshot_path) {
      const { data } = await supabase.storage.from("feedback-screenshots").createSignedUrl(fb.extra_screenshot_path, 3600);
      if (data?.signedUrl) urls.extra = data.signedUrl;
    }
    setSignedUrls(urls);
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          <Button size="sm" variant={!showDone ? "default" : "outline"} onClick={() => setShowDone(false)} className="text-xs">
            Open
          </Button>
          <Button size="sm" variant={showDone ? "default" : "outline"} onClick={() => setShowDone(true)} className="text-xs">
            All
          </Button>
        </div>
        {/* A Skeleton, not a blank space: an HTML-collapsed space reserves no height, so the
            row would still reflow when the count arrived. Its own testid, because the
            table-row skeleton renders in this same state and a test reaching for that one
            would pass with this branch deleted. */}
        {loading && (
          <Skeleton data-testid="feedback-count-loading" className="h-4 w-16" aria-hidden="true" />
        )}
        {showCount && (
          <p className="text-xs text-muted-foreground">
            {feedback.length} item{feedback.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="max-w-[300px]">Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Above feedback.map for the same reason as What's New: a failure notice under a
                  full table is below the fold. */}
              {loading && (
                <TableRow data-testid="feedback-loading">
                  <TableCell colSpan={7} className="py-12">
                    <Skeleton className="h-4 w-full" aria-hidden="true" />
                  </TableCell>
                </TableRow>
              )}
              {failed && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <QueryLoadError
                      failedText="Couldn't load feedback."
                      offlineText="You're offline. Feedback will load when your connection is back."
                      state={{ offline, canRetry }}
                      query={{ isFetching, refetch }}
                    />
                  </TableCell>
                </TableRow>
              )}
              {feedback.map((fb) => (
                <TableRow key={fb.id}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {format(new Date(fb.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-xs">{fb.user_email ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{categoryLabel[fb.category] ?? fb.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${priorityColor[fb.priority] ?? ""}`}>
                      {fb.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColor[fb.status] ?? ""}`}>
                      {fb.status.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate text-xs">{fb.description}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDetail(fb)} title="View">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {fb.status !== "done" && (
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7 text-green-600"
                        onClick={() => updateStatus.mutate({ id: fb.id, status: "done" })}
                        title="Mark complete"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {fb.status === "done" && (
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7 text-yellow-600"
                        onClick={() => updateStatus.mutate({ id: fb.id, status: "new" })}
                        title="Reopen"
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                      onClick={() => setDeleteId(fb.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !failed && feedback.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                    {!showDone && allFeedback.length > 0
                      ? "No open feedback — switch to All to see completed items."
                      : "No feedback submitted yet."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detailItem} onOpenChange={() => setDetailItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Feedback Detail</DialogTitle>
            <DialogDescription>
              Submitted {detailItem && format(new Date(detailItem.created_at), "PPpp")}
            </DialogDescription>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2">
                <Badge variant="outline">{categoryLabel[detailItem.category] ?? detailItem.category}</Badge>
                <Badge variant="outline" className={priorityColor[detailItem.priority] ?? ""}>{detailItem.priority}</Badge>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColor[detailItem.status] ?? ""}`}>
                  {detailItem.status.replace("_", " ")}
                </span>
              </div>
              <p className="text-foreground whitespace-pre-wrap">{detailItem.description}</p>
              {(detailItem.system_info as { target?: string } | null)?.target && (
                <p className="text-xs">
                  <span className="text-muted-foreground">Requested change: </span>
                  {(detailItem.system_info as { target?: string }).target}
                </p>
              )}
              {detailItem.page_url && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" /> Page: {detailItem.page_url}
                </p>
              )}
              {signedUrls.screenshot && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Auto-captured screenshot:</p>
                  <img src={signedUrls.screenshot} alt="Screenshot" className="rounded border max-h-64 w-full object-contain" />
                </div>
              )}
              {signedUrls.extra && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {detailItem.category === "content_change" ? "Desired image:" : "Attached file:"}
                  </p>
                  <img src={signedUrls.extra} alt="Extra" className="rounded border max-h-64 w-full object-contain" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete feedback?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (deleteId) { deleteFeedback.mutate(deleteId); setDeleteId(null); } }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ── Page ── */
export default function ChangelogPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Updates & Audit Log</h1>
          <p className="text-muted-foreground text-sm mt-1">
            What's new on the platform, plus a record of changes made in the system.
          </p>
        </div>

        <Tabs defaultValue="whats-new">
          <TabsList>
            <TabsTrigger value="whats-new">What's New</TabsTrigger>
            <TabsTrigger value="audit-log">Audit Log</TabsTrigger>
            {isAdmin && <TabsTrigger value="feedback">Feedback</TabsTrigger>}
          </TabsList>
          <TabsContent value="whats-new">
            <WhatsNewTab />
          </TabsContent>
          <TabsContent value="audit-log">
            <AuditLogTab />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="feedback">
              <FeedbackTab />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
